import cn from "classnames";
import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Props {
  title: string;
  children?: ReactNode;
  selector?: ReactNode;
  legend?: ReactNode;
  badge?: string;
  variant?: "bar" | "donut";
  isEmpty?: boolean;
  className?: string;
  id?: string;
  minimized?: boolean;
  onToggleMinimize?: () => void;
  draggable?: boolean;
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (event: React.DragEvent<HTMLElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd?: (event: React.DragEvent<HTMLElement>) => void;
  isDragging?: boolean;
  isDragOver?: boolean;
}

export const CrmChartCard = ({
  title,
  children,
  selector,
  legend,
  badge,
  variant = "bar",
  isEmpty = false,
  className,
  id,
  minimized = false,
  onToggleMinimize,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging = false,
  isDragOver = false,
}: Props) => {
  const intl = useIntl();

  return (
    <article
      id={id}
      className={cn("crm-chart-card", className, {
        "crm-chart-card--minimized": minimized,
        "crm-chart-card--dragging": isDragging,
        "crm-chart-card--drag-over": isDragOver,
      })}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <header className="crm-chart-card__header">
        <div className="crm-chart-card__title-wrap">
          {draggable && (
            <span
              className="crm-chart-card__drag-handle"
              title={intl.formatMessage({
                id: "app.metricsCrm.layout.drag",
              })}
              aria-hidden
            >
              <FontAwesomeIcon icon={["fas", "grip-vertical"]} />
            </span>
          )}
          <h3 className="crm-chart-card__title">{title}</h3>
          {badge && !minimized && (
            <span className="crm-chart-card__badge">{badge}</span>
          )}
        </div>
        <div className="crm-chart-card__actions">
          {selector && !minimized && (
            <div className="crm-chart-card__selector">{selector}</div>
          )}
          {onToggleMinimize && (
            <button
              type="button"
              className="crm-chart-card__action-btn"
              onClick={onToggleMinimize}
              title={intl.formatMessage({
                id: minimized
                  ? "app.metricsCrm.layout.expand"
                  : "app.metricsCrm.layout.minimize",
              })}
              aria-label={intl.formatMessage({
                id: minimized
                  ? "app.metricsCrm.layout.expand"
                  : "app.metricsCrm.layout.minimize",
              })}
            >
              <FontAwesomeIcon
                icon={["fas", minimized ? "plus" : "minus"]}
              />
            </button>
          )}
        </div>
      </header>

      {!minimized && (
        <div
          className={cn("crm-chart-card__body", {
            "crm-chart-card__body--bar": variant === "bar",
            "crm-chart-card__body--donut": variant === "donut",
          })}
        >
          {isEmpty ? (
            <div className="crm-chart-card__empty">
              <div className="crm-chart-card__empty-icon">
                <FontAwesomeIcon icon={["fas", "chart-pie"]} />
              </div>
              <p>{intl.formatMessage({ id: "app.analiyticsFace.noDate" })}</p>
            </div>
          ) : variant === "donut" ? (
            <>
              <div className="crm-chart-card__chart-area">{children}</div>
              {legend && (
                <div className="crm-chart-card__legend-area">{legend}</div>
              )}
            </>
          ) : (
            <div className="crm-chart-card__bar-area">{children}</div>
          )}
        </div>
      )}
    </article>
  );
};
